import dayjs from 'dayjs';
import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

import { useAdminGetDashboardCollabsDataQuery } from '../../hooks/reactQuery/dashboard/useAdminGetDashboardCollabsDataQuery';
import { icons } from '../../themes/icons';
import { formatNumberWithCommas } from '../../utils/converters';
import { calculatePercentageChange } from '../../utils/dashboard';
import { toLocaleString } from '../../utils/format-number';
import StatCard from './StatCard';

type Props = {
  fromDate: string | null;
  priorFrom: string | null;
  toDate: string | null;
  priorTo: string | null;
  selectedLabel: string;
  marketID: string | null;
};

export const Collabs: React.FC<Props> = ({
  fromDate,
  toDate,
  priorFrom,
  priorTo,
  selectedLabel,
  marketID,
}) => {
  const { data, isFetching } = useAdminGetDashboardCollabsDataQuery({
    fromDate: fromDate || '',
    toDate: toDate || '',
    priorFromDate: priorFrom || '',
    priorToDate: priorTo || '',
    marketID,
  });

  const { sentPercentage, collabRevenuePercentage } = React.useMemo(() => {
    const sentPercentage = calculatePercentageChange(
      data?.totalVenuesSentInvitesCurrent || 0,
      data?.totalVenuesSentInvitesPrior || 0,
    );

    const collabRevenuePercentage = calculatePercentageChange(
      data?.totalCollabsRevenueCurrent || 0,
      data?.totalCollabsRevenuePrior || 0,
    );

    return { sentPercentage, collabRevenuePercentage };
  }, [data]);

  return (
    <BoxContainer>
      <AllStats>
        <Heading>
          <Link to={'/collabs'} style={{ color: 'inherit', textDecoration: 'none' }}>
            Collabs
          </Link>
        </Heading>
        <Time>{selectedLabel}</Time>
      </AllStats>

      <StatCard
        isFetching={isFetching}
        isDesktop
        name="Venues sending Collabs"
        count={toLocaleString(data?.totalVenuesSentInvitesCurrent || 0)}
        compare={`${sentPercentage}%`}
        icon={<icons.EnvelopIcon />}
        percentageNumber={sentPercentage}
      />
      <StatCard
        isFetching={isFetching}
        isDesktop
        name="Collabs revenue"
        count={`$${formatNumberWithCommas(data?.totalCollabsRevenueCurrent || 0)}`}
        compare={`${collabRevenuePercentage}%`}
        icon={<icons.DollarIcon />}
        rightAlignItems={'flex-end'}
        percentageNumber={collabRevenuePercentage}
      />
      <StatCard
        isFetching={isFetching}
        isDesktop
        name="Collabs completed"
        count={toLocaleString(data?.totalCompletedCollabsCurrent || 0)}
        icon={<icons.CollabCompleteIcon />}
      />
      <StatCard
        isFetching={isFetching}
        isDesktop
        name="Collabs sent"
        count={toLocaleString(
          (data?.totalAcceptedCollabs || 0) + (data?.totalDeclinedCollabs || 0),
        )}
        icon={<icons.EnvelopIcon />}
      />
      <NestedBlock>
        <StatCard
          isFetching={isFetching}
          isDesktop
          name="Collabs accepted"
          count={toLocaleString(data?.totalAcceptedCollabs || 0)}
          icon={<icons.CollabCompleteIcon />}
        />
        <StatCard
          isFetching={isFetching}
          isDesktop
          name="Collabs declined"
          count={toLocaleString(data?.totalDeclinedCollabs || 0)}
          icon={<icons.CollabCompleteIcon />}
        />
      </NestedBlock>
    </BoxContainer>
  );
};

const BoxContainer = styled.div`
  margin-top: 20px;
  border: 1px solid #00000014;
  border-radius: 8px;
  padding: 30px;
`;
const AllStats = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #0000000d;
  padding-bottom: 15px;
`;

const Heading = styled.div`
  color: #111111;
  font-size: 18px;
  font-family: Poppins;
  font-weight: 500;
`;

const Time = styled.div`
  color: rgba(0, 0, 0, 0.5);
  font-size: 13px;
  font-family: Poppins;
  font-weight: 500;
`;
const NestedBlock = styled.div`
  margin-left: 40px;
`;
